import React from 'react';

import { Alert, Box, Card, CardContent, CircularProgress, Stack, Typography } from '@mui/material';

import type { AnalyticsSearchCounts } from '~/services/AnalyticsService';

const SEARCH_COUNT_ROWS = [
  { key: 'allSearches', label: 'All searches' },
  { key: 'exactGeoOrVendorMatches', label: 'Exact geo or vendor matches' },
  { key: 'emptyQuerySearches', label: 'Empty query searches' },
  { key: 'remainingSearches', label: 'Remaining searches' },
] as const satisfies ReadonlyArray<{ key: keyof AnalyticsSearchCounts; label: string }>;

type SearchCountsBarProps = {
  data: AnalyticsSearchCounts | undefined;
  isLoading: boolean;
  error: Error | null;
  action?: React.ReactNode;
};

export default function SearchCountsBar({ data, isLoading, error, action }: SearchCountsBarProps) {
  let content: React.ReactNode;

  if (isLoading) {
    content = (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 3,
          gap: 2,
        }}
      >
        <CircularProgress size={32} />
        <Typography variant="body2">Fetching counts from BigQuery</Typography>
      </Box>
    );
  } else if (error) {
    content = <Alert severity="error">{error.message}</Alert>;
  } else if (!data) {
    content = (
      <Typography variant="body2" color="text.secondary">
        No counts
      </Typography>
    );
  } else {
    content = (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ flexWrap: 'wrap' }}>
          {SEARCH_COUNT_ROWS.map(({ key, label }) => (
            <Box key={key}>
              <Typography variant="caption" color="text.secondary" display="block">
                {label}
              </Typography>
              <Typography variant="h6" component="span">
                {data[key].toLocaleString()}
              </Typography>
            </Box>
          ))}
        </Stack>
        {action && <Box sx={{ ml: { xs: 0, sm: 'auto' } }}>{action}</Box>}
      </Box>
    );
  }

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>{content}</CardContent>
    </Card>
  );
}
